import type {
  AppointmentPlanningPreviewRow,
  AppointmentPlanningRouteAnchor,
  AppointmentPlanningRouteAnchorKind,
} from "../../../../../../../../../../api/kehrblatt/planning"
import type {
  CalendarBoardPropertyRef,
  CalendarBoardWorkOrderRef,
  CalendarRouteTransition,
} from "../../../../../../../../../../api/kehrblatt/schedule-events"
import { internalOrderTypeLabel } from "../../../../../../../../../../_shared/_logic/order-type-labels"
import type { AppointmentPlanningRouteAnchorUpdatesByRowId } from "../../../../../_shared/_logic/calendar-contract"

type BuildPlanningRouteAnchorUpdatesInput = {
  rows: AppointmentPlanningPreviewRow[]
  events: ScheduleEvent[]
  workOrderById: Record<string, CalendarBoardWorkOrderRef>
  propertyById: Record<string, CalendarBoardPropertyRef>
  transitions: CalendarRouteTransition[]
}

type TransitionSide = "previous" | "next"

const PREVIEW_EVENT_PREFIX = "preview-event:"

const rowIdFromPreviewEventId = (eventId?: string | null): string | null => {
  const value = String(eventId ?? "")

  return value.startsWith(PREVIEW_EVENT_PREFIX) ? value.slice(PREVIEW_EVENT_PREFIX.length) : null
}

const isOfficeAnchor = (anchor: AppointmentPlanningRouteAnchor | null | undefined) =>
  anchor?.anchor_kind === "office_start" || anchor?.anchor_kind === "office_end"

const compactAddressLabel = (property?: CalendarBoardPropertyRef | null): string | null => {
  if (!property) return null

  const streetLine = [property.street, property.house_number]
    .map((part) => String(part ?? "").trim())
    .filter(Boolean)
    .join(" ")
  const addressExtra = String(property.address_extra ?? "").trim()
  const cityLine = [property.zip, property.city]
    .map((part) => String(part ?? "").trim())
    .filter(Boolean)
    .join(" ")

  return [streetLine, addressExtra, cityLine].filter(Boolean).join(", ") || null
}

const anchorKindForEvent = (event: ScheduleEvent): AppointmentPlanningRouteAnchorKind => {
  switch (String(event.event_type ?? "").toUpperCase()) {
    case "INTERNAL_BLOCK":
      return "internal_block"
    case "ABSENCE":
      return "absence"
    case "APPOINTMENT":
    default:
      return "appointment"
  }
}

const anchorTimes = (transition: CalendarRouteTransition, side: TransitionSide) =>
  side === "previous"
    ? {
        start_at: transition.previous_anchor_start_at,
        end_at: transition.previous_anchor_end_at,
      }
    : {
        start_at: transition.next_anchor_start_at,
        end_at: transition.next_anchor_end_at,
      }

const anchorLabel = (
  event: ScheduleEvent,
  workOrder: CalendarBoardWorkOrderRef | undefined,
  transition: CalendarRouteTransition,
  side: TransitionSide,
): string | null => {
  const transitionLabel =
    side === "previous" ? transition.previous_event_label : transition.next_event_label
  const eventTitle = String(event.title ?? "").trim()

  return transitionLabel || eventTitle || internalOrderTypeLabel(workOrder)
}

const anchorFromEvent = ({
  event,
  transition,
  side,
  workOrderById,
  propertyById,
}: {
  event: ScheduleEvent
  transition: CalendarRouteTransition
  side: TransitionSide
  workOrderById: Record<string, CalendarBoardWorkOrderRef>
  propertyById: Record<string, CalendarBoardPropertyRef>
}): AppointmentPlanningRouteAnchor => {
  const workOrder = event.work_order_id ? workOrderById[event.work_order_id] : undefined
  const propertyId = event.property_id ?? workOrder?.property_id ?? null
  const property = propertyId ? propertyById[propertyId] : undefined
  const times = anchorTimes(transition, side)

  return {
    schedule_event_id: event.id ?? null,
    work_order_id: event.work_order_id ?? null,
    start_at: times.start_at ?? null,
    end_at: times.end_at ?? null,
    employee_id: event.employee_id ?? transition.employee_id ?? null,
    address_label: compactAddressLabel(property),
    title: anchorLabel(event, workOrder, transition, side),
    distance_km: transition.distance_km,
    anchor_kind: anchorKindForEvent(event),
    estimated_travel_minutes:
      transition.rounded_travel_minutes ?? transition.estimated_travel_minutes ?? null,
    travel_time_estimated: Boolean(transition.missing_coordinates),
  }
}

export const buildPlanningRouteAnchorUpdatesFromTransitions = ({
  rows,
  events,
  workOrderById,
  propertyById,
  transitions,
}: BuildPlanningRouteAnchorUpdatesInput): AppointmentPlanningRouteAnchorUpdatesByRowId => {
  const updates: AppointmentPlanningRouteAnchorUpdatesByRowId = {}
  const eventById = new Map<string, ScheduleEvent>()
  for (const event of events) {
    if (event.id) eventById.set(event.id, event)
  }

  for (const row of rows) {
    if (row.status !== "planned" || !row.appointment) continue

    updates[row.client_row_id] = {
      previous_anchor: isOfficeAnchor(row.appointment.previous_anchor)
        ? row.appointment.previous_anchor
        : null,
      next_anchor: isOfficeAnchor(row.appointment.next_anchor) ? row.appointment.next_anchor : null,
    }
  }

  for (const transition of transitions) {
    const previousRowId = rowIdFromPreviewEventId(transition.previous_event_id)
    const nextRowId = rowIdFromPreviewEventId(transition.next_event_id)
    const previousEvent = eventById.get(transition.previous_event_id)
    const nextEvent = eventById.get(transition.next_event_id)

    if (nextRowId && updates[nextRowId] && previousEvent) {
      updates[nextRowId].previous_anchor = anchorFromEvent({
        event: previousEvent,
        transition,
        side: "previous",
        workOrderById,
        propertyById,
      })
    }

    if (previousRowId && updates[previousRowId] && nextEvent) {
      updates[previousRowId].next_anchor = anchorFromEvent({
        event: nextEvent,
        transition,
        side: "next",
        workOrderById,
        propertyById,
      })
    }
  }

  return updates
}
